


import cvData from '../../../data/cv'

/**
 * Wait until every <img> inside the given root has finished loading (or failed),
 * so the printed PDF never captures half-loaded logos/icons. Images that error
 * resolve too — the components fall back to a lettermark/glyph on error, and we
 * don't want a single dead logo source to block the whole export.
 */
function waitForImages(root: ParentNode, timeoutMs = 6000): Promise<void> {
  const imgs = Array.from(root.querySelectorAll('img'))
  const pending = imgs.filter((img) => !(img.complete && img.naturalWidth > 0))
  if (pending.length === 0) return Promise.resolve()

  return new Promise<void>((resolve) => {
    let remaining = pending.length
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }
    const settle = () => {
      remaining -= 1
      if (remaining <= 0) finish()
    }
    pending.forEach((img) => {
      img.addEventListener('load', settle, { once: true })
      img.addEventListener('error', settle, { once: true })
    })
    // Safety net: never hang the export waiting on a slow/broken CDN.
    window.setTimeout(finish, timeoutMs)
  })
}

/**
 * Export the CV as a REAL, text-based PDF via the browser's native print
 * pipeline ("Save as PDF"). This produces selectable/searchable text and — via
 * the @media print rules in index.css — maps each rendered .cv-page sheet to
 * exactly one physical A4 page, so the PDF matches the on-screen page split.
 *
 * Before printing we wait for webfonts AND all logo/icon images to finish
 * loading, otherwise the browser snapshots the DOM with blank images.
 *
 * @param fileName  suggested document name (browsers use it as the default
 *                  "Save as PDF" filename via document.title)
 */
export async function exportCvToPdf(fileName: string): Promise<void> {
  // Ensure webfonts are ready so the printed layout matches the screen.
  if ((document as any).fonts?.ready) {
    try {
      await (document as any).fonts.ready
    } catch {
      /* non-fatal */
    }
  }

  // Ensure every logo/icon (including those on off-screen pages) has loaded.
  const cvRoot = document.querySelector('.cv-view') ?? document
  await waitForImages(cvRoot)

  const prevTitle = document.title
  // Browsers seed the "Save as PDF" filename from the document title.
  document.title =
    fileName || `${cvData.profile.name} — ${cvData.meta.documentLabel}`

  const restore = () => {
    document.title = prevTitle
    window.removeEventListener('afterprint', restore)
  }
  window.addEventListener('afterprint', restore)

  window.print()

  // Fallback restore in case afterprint doesn't fire (some browsers).
  window.setTimeout(restore, 1000)
}



